import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Clock, Smartphone, Star, Gamepad2, ShieldCheck, Heart, ShieldAlert } from 'lucide-react';
import { productApi } from '@/api-client';
import type { Product } from '@/types/product';
import { getPriceStr } from '@/types/product';
import { SEO } from '@/components/SEO';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.1, duration: 0.5, ease: 'easeOut' as const },
  }),
};

export function Home() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<{ views: number; visitors: number } | null>(null);
  const hasCounted = useRef(false);

  // ═══ AGE GATE – sessionStorage (bei jedem neuen Besuch/Tab) ═══
  const [ageVerified, setAgeVerified] = useState(() => {
    try { return sessionStorage.getItem('jl-age-verified') === 'true'; } catch { return false; }
  });
  const [showAgeGate, setShowAgeGate] = useState(false);

  useEffect(() => {
    // Prüfe bei jedem Seitenaufruf (neue Session = neue Abfrage)
    const verified = sessionStorage.getItem('jl-age-verified') === 'true';
    if (!verified) {
      setShowAgeGate(true);
    }
    setAgeVerified(verified);
  }, []);

  const confirmAge = () => {
    try { sessionStorage.setItem('jl-age-verified', 'true'); } catch {}
    setAgeVerified(true);
    setShowAgeGate(false);
  };

  const rejectAge = () => {
    // Unter 18: Modal schließen, 18+ bleiben überall ausgeblendet
    setShowAgeGate(false);
  };

  useEffect(() => {
    productApi.list().then(data => {
      // ═══ JUGENDSCHUTZ: 18+ Produkte nur anzeigen wenn verifiziert ═══
      const allProducts = Array.isArray(data) ? data : [];
      const visible = ageVerified 
        ? allProducts 
        : allProducts.filter(p => !(p as any).isAdult);
      setProducts(visible.slice(0, 3));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [ageVerified]);

  useEffect(() => {
    if (hasCounted.current) return;
    hasCounted.current = true;
    fetch('/api/counter.php')
      .then(res => res.json())
      .then(data => setStats(data))
      .catch(() => setStats(null));
  }, []);

  const line1Words = ["Software", "&", "Spiele", "mit"];
  const line2Words = ["Leidenschaft"];
  const baseDelay = 0.3;
  const wordDelay = 0.28;

  const renderWords = (words: string[], startIndex: number, className?: string) =>
    words.map((word, i) => (
      <motion.span
        key={`word-${startIndex + i}`}
        className={`inline-block mx-[0.18em] ${className || ''}`}
        initial={{ opacity: 0, y: -350, scale: 1.3 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{
          type: 'spring',
          stiffness: 80,
          damping: 9,
          mass: 3,
          delay: baseDelay + (startIndex + i) * wordDelay,
        }}
      >
        {word}
      </motion.span>
    ));

  return (
    <>
      <SEO 
        title="Startseite" 
        description="Exklusive Lifestyle-Apps & Erotik-Spiele für Paare und die Swinger-Szene. Entdecken Sie LustDuell und weitere diskrete Software-Lösungen."
        keywords="Lifestyle-Apps für Paare, Erotik-Spiele Android, Swinger Lifestyle App, LustDuell, Diskrete Software Paare, JL-Software"
        path="/" 
      />

      {/* ═══ GLOBALES AGE GATE MODAL ═══ */}
      <AnimatePresence>
        {showAgeGate && (
          <motion.div 
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm px-4"
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              className="bg-surface border border-crimson/30 rounded-2xl p-8 max-w-md w-full text-center shadow-2xl"
            >
              <ShieldAlert size={48} className="text-crimson mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-text-primary mb-2">Altersbeschränkung</h2>
              <p className="text-text-secondary mb-2 text-sm leading-relaxed">
                Diese Website enthält Inhalte, die nur für Erwachsene ab 18 Jahren bestimmt sind.
              </p>
              <p className="text-text-muted mb-6 text-xs">
                Mit dem Betritt bestätigen Sie, dass Sie das 18. Lebensjahr vollendet haben. 
                Bei Falschangabe liegt eine Straftat vor.
              </p>
              <div className="flex gap-3 justify-center">
                <button 
                  onClick={rejectAge}
                  className="px-5 py-2.5 text-text-secondary font-medium rounded-xl hover:bg-white/[0.04] transition-all border border-white/[0.06]"
                >
                  Nein, unter 18
                </button>
                <button 
                  onClick={confirmAge}
                  className="px-5 py-2.5 bg-crimson text-white font-bold rounded-xl hover:bg-crimson/80 transition-all"
                >
                  Ja, ich bin über 18
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <section className="relative mt-[70px] pt-24 pb-20 md:pt-32 md:pb-24 text-center overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] md:w-[600px] md:h-[600px] rounded-full bg-gold/[0.06] blur-3xl pointer-events-none" />
        <motion.div initial="hidden" animate="visible" className="relative z-10 max-w-4xl mx-auto px-6">
          <motion.div custom={0} variants={fadeUp}
            className="inline-flex items-center gap-2 bg-gold-dim border border-gold/20 text-gold px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider mb-6">
            <span className="w-1.5 h-1.5 bg-gold rounded-full animate-pulse" /> Premium Indie-Entwicklung
          </motion.div>
          
          <h1 className="font-display text-4xl md:text-6xl lg:text-7xl font-bold italic text-glow leading-tight mb-6">
            <span className="block">{renderWords(line1Words, 0)}</span>
            <span className="block text-gold">{renderWords(line2Words, line1Words.length)}</span>
          </h1>

          <div className="text-text-secondary text-lg md:text-xl font-light leading-relaxed max-w-2xl mx-auto mb-8 overflow-hidden">
            <motion.span
              className="block"
              initial={{ x: "-100vw", skewX: -14 }}
              animate={{ x: 0, skewX: [-14, -14, 10, 0] }}
              transition={{
                x: { duration: 1.3, ease: "easeOut" },
                skewX: { duration: 1.3, times: [0, 0.6, 0.82, 1], ease: "easeOut" }
              }}
            >
              Diskrete Lifestyle-Apps und hochwertige Erotik-Spiele für Paare.
            </motion.span>

            <motion.span
              className="block"
              initial={{ x: "100vw", skewX: 14 }}
              animate={{ x: 0, skewX: [14, 14, -10, 0] }}
              transition={{
                x: { duration: 1.3, ease: "easeOut", delay: 0.35 },
                skewX: { duration: 1.3, times: [0, 0.6, 0.82, 1], ease: "easeOut", delay: 0.35 }
              }}
            >
              Entdecken Sie digitale Erlebnisse, die Ihre Beziehung bereichern.
            </motion.span>
          </div>

          <motion.div custom={3} variants={fadeUp} className="flex flex-wrap items-center justify-center gap-4">
            <Link to="/shop" className="inline-flex items-center gap-2 px-7 py-3 bg-gold text-surface-primary font-semibold rounded-xl shadow-gold hover:shadow-gold-lg hover:-translate-y-0.5 transition-all">
              Zum App-Shop entdecken ↓
            </Link>
            <Link to="/ueber-uns" className="inline-flex items-center gap-2 px-7 py-3 border border-white/[0.06] text-text-secondary font-semibold rounded-xl hover:border-gold/25 hover:text-text-primary hover:bg-white/[0.03] transition-all">
              Über JL-Software
            </Link>
          </motion.div>
        </motion.div>
      </section>

      <section className="border-y border-white/[0.06] bg-surface-secondary">
        <div className="max-w-[1200px] mx-auto px-6 py-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Heart, title: 'Lifestyle & Erotik', desc: 'Anspruchsvolle Apps für Paare und die Swinger-Szene – ästhetisch und ohne billige Klischees.' },
              { icon: Gamepad2, title: 'LustDuell & Mehr', desc: 'Unsere Spiele fördern spielerisch die Intimität und den Austausch in der Partnerschaft.' },
              { icon: Smartphone, title: 'Absolute Diskretion', desc: 'Keine Cloud-Zwänge, kein Tracking. Ihre privaten Momente bleiben auf Ihrem Gerät.' },
              { icon: ShieldCheck, title: 'Made in Germany', desc: 'Ehrliche Software-Entwicklung aus Rostock. Sicher, transparent und DSGVO-konform.' },
            ].map((feature, i) => (
              <motion.div key={feature.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="group p-6 rounded-2xl border border-white/[0.06] bg-surface hover:border-gold/20 hover:-translate-y-1 transition-all duration-300">
                <div className="w-10 h-10 rounded-xl bg-gold-dim flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <feature.icon size={20} className="text-gold" />
                </div>
                <h3 className="text-sm font-bold text-text-primary mb-2">{feature.title}</h3>
                <p className="text-xs text-text-secondary leading-relaxed">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-[1200px] mx-auto px-6 py-20">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
          <span className="inline-block text-crimson text-xs font-bold uppercase tracking-[0.15em] mb-3">Portfolio</span>
          <h2 className="text-3xl md:text-4xl font-extrabold text-text-primary mb-3">Unsere Highlights</h2>
          <p className="text-text-secondary max-w-md mx-auto">Hochwertige Software-Lösungen für anspruchsvolle Nutzer.</p>
        </motion.div>

        {loading ? (
          <div className="text-center py-12"><div className="animate-spin w-8 h-8 border-2 border-gold border-t-transparent rounded-full mx-auto" /></div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((product, i) => (
                <motion.div key={product.id} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                  <ProductCard product={product} />
                </motion.div>
              ))}
              <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.3 }}
                className="bg-surface rounded-3xl border border-white/[0.06] overflow-hidden opacity-60 flex flex-col">
                <div className="h-[220px] flex items-center justify-center bg-gradient-to-br from-[#0f0f1a] via-[#1a1a2e] to-[#0f0f1a] relative">
                  <span className="font-display text-5xl font-bold italic text-text-muted">???</span>
                  <span className="absolute top-4 right-4 px-3 py-1.5 rounded-full text-xs font-bold bg-white/5 text-text-muted border border-white/10">In Arbeit</span>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <h3 className="text-xl font-bold text-text-muted mb-2">Nächste Innovation</h3>
                  <p className="text-text-muted text-sm leading-relaxed flex-1">Wir arbeiten bereits an der nächsten App für den Lifestyle-Bereich. Seien Sie gespannt!</p>
                  <div className="flex items-center justify-between pt-5 mt-5 border-t border-white/[0.06]">
                    <span className="text-text-muted font-bold text-2xl">–</span>
                    <span className="px-5 py-2.5 rounded-xl text-sm font-semibold bg-white/[0.03] text-text-muted border border-white/[0.06]">Coming Soon</span>
                  </div>
                </div>
              </motion.div>
            </div>
            <div className="text-center mt-12">
              <Link to="/shop" className="inline-flex items-center gap-2 px-6 py-3 border border-white/[0.06] text-text-secondary font-semibold rounded-xl hover:border-gold/25 hover:text-text-primary hover:bg-white/[0.03] transition-all">
                Alle Apps & Scripts ansehen →
              </Link>
            </div>
          </>
        )}
      </section>

      <section className="border-t border-white/[0.06] bg-surface-secondary">
        <div className="max-w-[1200px] mx-auto px-6 py-10">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-10">
            {stats ? (
              <>
                <div className="text-center">
                  <div className="font-display text-4xl md:text-5xl font-bold italic text-gold">{stats.visitors}</div>
                  <div className="text-xs uppercase tracking-[0.15em] text-text-muted mt-1">Interessenten</div>
                </div>
                <div className="hidden sm:block w-px h-12 bg-white/[0.08]" />
                <div className="text-center">
                  <div className="font-display text-4xl md:text-5xl font-bold italic text-gold">{stats.views}</div>
                  <div className="text-xs uppercase tracking-[0.15em] text-text-muted mt-1">Seitenaufrufe</div>
                </div>
              </>
            ) : (
              <div className="flex items-center gap-2 text-text-muted text-sm">
                <div className="animate-spin w-4 h-4 border-2 border-gold border-t-transparent rounded-full" />
                Analysedaten werden geladen...
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
}

function ProductCard({ product }: { product: Product }) {
  const features = Array.isArray(product.features) ? product.features : [];
  return (
    <div className="group bg-surface rounded-3xl border border-white/[0.06] overflow-hidden transition-all duration-400 hover:-translate-y-2 hover:border-gold/25 hover:shadow-card gradient-border flex flex-col h-full">
      <div className="h-[220px] relative overflow-hidden">
        <img src={product.image} alt={product.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          onError={e => { (e.target as HTMLImageElement).src = '/images/placeholder.jpg'; }} />
        <div className="absolute inset-0 bg-gradient-to-t from-surface to-transparent opacity-60" />
        {product.badge && (
          <span className={`absolute top-4 right-4 px-3 py-1.5 rounded-full text-xs font-bold backdrop-blur-md ${
            product.badgeColor === 'crimson' ? 'bg-crimson-dim text-crimson border border-crimson/30' : 'bg-gold-dim text-gold border border-gold/30'
          }`}>{product.badge}</span>
        )}
      </div>
      <div className="p-6 flex flex-col flex-1">
        <h3 className="text-xl font-bold text-text-primary mb-2">{product.name}</h3>
        <p className="text-text-secondary text-sm leading-relaxed flex-1 mb-4">{product.description}</p>
        {features.length > 0 && (
          <div className="flex flex-wrap gap-3 mb-4">
            {features.map(f => (
              <span key={f} className="flex items-center gap-1.5 text-xs text-text-muted">
                {f === '2–4 Spieler' && <Users size={14} />}
                {f.includes('Min') && <Clock size={14} />}
                {f === 'Android' && <Smartphone size={14} />}
                {f === 'Soundtrack' && <Star size={14} />}
                {f}
              </span>
            ))}
          </div>
        )}
        <div className="flex items-center justify-between pt-5 border-t border-white/[0.06]">
          <span className="text-2xl font-extrabold text-gold">{getPriceStr(product.price)} <span className="text-sm font-medium text-text-muted">€</span></span>
          <Link to={`/shop/${product.slug}`} className="px-5 py-2.5 rounded-xl text-sm font-semibold bg-gold-dim text-gold border border-gold/20 hover:bg-gold/25 hover:translate-x-1 transition-all">
            Details ansehen →
          </Link>
        </div>
      </div>
    </div>
  );
}